import { Badge } from '@creanizer/ui'
import { Sparkles, Zap, Flame, Volume2, Timer, Move } from 'lucide-react'
import type { SkillPerformance } from '@creanizer/types'

interface SkillPerformanceVisualProps {
  performance: SkillPerformance
}

// 日本語の色名を英語/HEXに変換
function convertColorName(color: string): string {
  const colorMap: Record<string, string> = {
    '赤': '#DC2626',
    '青': '#2563EB',
    '緑': '#16A34A',
    '黄': '#EAB308',
    '黄色': '#EAB308',
    '紫': '#9333EA',
    '橙': '#EA580C',
    'オレンジ': '#EA580C',
    '桃': '#EC4899',
    'ピンク': '#EC4899',
    '白': '#FFFFFF',
    '黒': '#000000',
    '灰': '#6B7280',
    'グレー': '#6B7280',
    '茶': '#92400E',
    '茶色': '#92400E',
    '金': '#FCD34D',
    '金色': '#FCD34D',
    '銀': '#C0C0C0',
    '銀色': '#C0C0C0',
    '水色': '#67E8F9',
    '紺': '#1E3A8A',
    '藍': '#312E81',
    '翠': '#059669',
    '朱': '#DC2626',
    '炎': '#EA580C',
    '氷': '#BFDBFE',
    '雷': '#FCD34D',
    '闇': '#1F2937',
    '光': '#FEF3C7',
    '虹': 'linear-gradient(90deg, #EF4444, #F59E0B, #EAB308, #84CC16, #06B6D4, #3B82F6, #8B5CF6)'
  }
  
  // すでに英語の色名やHEXコードの場合はそのまま返す
  if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('linear') || /^[a-zA-Z]+$/.test(color)) {
    return color
  }
  
  // 日本語の色名を変換
  return colorMap[color] || '#94A3B8' // デフォルトはスレートグレー
}

export function SkillPerformanceVisual({ performance }: SkillPerformanceVisualProps) {
  return (
    <div className="space-y-4">
      {/* 視覚効果 */}
      <div className="card">
        <div className="mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            視覚効果
          </h4>
        </div>
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-0.5">準備</Badge>
              <p className="text-sm flex-1">{performance.visual.preparationEffect}</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-0.5">実行</Badge>
              <p className="text-sm flex-1">{performance.visual.executionEffect}</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-0.5">着弾</Badge>
              <p className="text-sm flex-1">{performance.visual.impactEffect}</p>
            </div>
          </div>
          
          <div className="pt-4 border-t">
            <p className="text-sm font-semibold mb-3">カラースキーム</p>
            <div className="flex gap-2 items-center flex-wrap">
              {performance.visual.colorScheme.map((color, i) => {
                const convertedColor = convertColorName(color)
                const isGradient = convertedColor.includes('gradient')
                return (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div
                      className="w-10 h-10 rounded-lg border-2 shadow-sm"
                      style={isGradient ? { background: convertedColor } : { backgroundColor: convertedColor }}
                      title={color}
                    />
                    <span className="text-xs text-muted-foreground">{color}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* アニメーション */}
      <div className="card">
        <div className="mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Move className="w-4 h-4" />
            アニメーション
          </h4>
        </div>
        <div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
            
            {/* 準備姿勢 */}
            <div className="relative flex items-start mb-6 ml-2">
              <div className="absolute left-4 w-4 h-4 rounded-full bg-blue-500 -translate-x-1/2 ring-4 ring-background" />
              <div className="ml-10 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">準備姿勢</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {performance.animation.preparationPose}
                </p>
              </div>
            </div>
            
            {/* 実行動作 */}
            <div className="relative flex items-start mb-6 ml-2">
              <div className="absolute left-4 w-4 h-4 rounded-full bg-orange-500 -translate-x-1/2 ring-4 ring-background" />
              <div className="ml-10 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">実行動作</span>
                  <Badge variant="secondary" className="text-xs">
                    <Timer className="w-3 h-3 mr-1" />
                    {performance.animation.duration}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {performance.animation.executionMovement}
                </p>
              </div>
            </div>
            
            {/* 終了姿勢 */}
            {performance.animation.recoveryPose && (
              <div className="relative flex items-start mb-6 ml-2">
                <div className="absolute left-4 w-4 h-4 rounded-full bg-green-500 -translate-x-1/2 ring-4 ring-background" />
                <div className="ml-10 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">終了姿勢</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {performance.animation.recoveryPose}
                  </p>
                </div>
              </div>
            )}
            
          </div>
        </div>
      </div>
      
      {/* 音響効果 */}
      <div className="card">
        <div className="mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            音響効果
          </h4>
        </div>
        <div>
          <div className="space-y-3">
            {performance.audio.preparationSound && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <Badge variant="outline" className="self-start shrink-0">準備音</Badge>
                <p className="text-sm">{performance.audio.preparationSound}</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-start gap-2">
              <Badge variant="outline" className="self-start shrink-0">発動音</Badge>
              <p className="text-sm">{performance.audio.executionSound}</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start gap-2">
              <Badge variant="outline" className="self-start shrink-0">命中音</Badge>
              <p className="text-sm">{performance.audio.impactSound}</p>
            </div>
            {performance.audio.ambientSound && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <Badge variant="outline" className="self-start shrink-0">環境音</Badge>
                <p className="text-sm">{performance.audio.ambientSound}</p>
              </div>
            )}
            {performance.audio.voiceLines && performance.audio.voiceLines.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <Badge variant="outline" className="self-start shrink-0">ボイスライン</Badge>
                <p className="text-sm italic">"{performance.audio.voiceLines[0]}"</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  )
}